import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { surveyAPI } from '../services/api';
import type { Survey } from '../types';

const AllCurrentSurveys: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const res = await surveyAPI.getSurveys();
        const list: Survey[] = res.data || [];
        const nonTemplates = list.filter((s: any) => !(s as any).isTemplate);
        const now = new Date();
        const current = nonTemplates.filter((s: any) => {
          if (!(s as any).isActive) return false;
          const start = new Date((s as any).startDate);
          const end = new Date((s as any).endDate);
          return now >= start && now <= end;
        });
        setSurveys(current);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch surveys');
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/surveys')}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to Surveys
        </Button>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              All Current Surveys
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View all surveys that are currently active and running for your organisation.
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {surveys.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No current surveys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When a survey is active, it will appear here.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {surveys.map((survey: any) => (
            <Grid item xs={12} sm={6} md={4} key={survey._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: 1,
                  transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {survey.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {survey.description || 'No description provided'}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    {formatDate(survey.startDate)} – {formatDate(survey.endDate)}
                  </Typography>
                  <Chip
                    label="Active"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    variant="contained"
                    fullWidth
                    onClick={() =>
                      navigate(isManager ? `/surveys/${survey._id}/responses` : `/surveys/${survey._id}/take`)
                    }
                  >
                    {isManager ? 'View' : 'Take Survey'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default AllCurrentSurveys;
